import React, { useState, useEffect, useCallback } from 'react'
import { colors } from './fieldTheme'
import { TOUR_STATES, zoneOf, zoneLabel, whenLabel, reportLines, latestLabel } from './fieldTours'
import { shortName } from './fieldLabels'
import { plural } from './fieldReference'
import { ChevronLeft, ChevronRight, CheckIcon, WrenchIcon, CameraIcon, CommentIcon, InventoryIcon } from './FieldIcons'
import { SecondaryButton, PhotoViewer } from './FieldUI'

// Compte rendu d'un emplacement dans le tour (maquette « Compte rendu du tour », variante A, 24/10/2026) :
// écran plein, rangé par nature et non plus par heure. Rubriques toujours dans le même ordre, l'action
// d'abord : Interventions, Inventaire, Photos, Commentaires ; une rubrique vide garde sa place avec une
// ligne grise. Heure et auteur au second plan, la chronologie complète repliée en bas, en fil d'actualité.
// Non repris de la maquette, faute de donnée fiable aujourd'hui [À VÉRIFIER] : statut des interventions
// (il vit dans le module Interventions), liste des objets en écart (dans l'inventaire enregistré),
// interventions et photos qu'on touche pour les ouvrir.

// Bandeau d'état aux couleurs du plan : vert « Fait », jaune « Fait avec intervention », gris sinon.
// Déjà fait : tout le bandeau ouvre le compte rendu (ligne à chevron, pas de lien souligné).
const BANNER_BACKGROUNDS = { todo: '#EEF0F5', done: '#DDF7EA', intervention: '#FFF1D1', inventory: '#FDE4DF' }

export function StatusBanner({ status, onOpen })
{
	const look = TOUR_STATES[status.state]
	const content = (
		<React.Fragment>
			<span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: look.background, color: look.color, boxShadow: status.state === 'todo' ? 'inset 0 0 0 2px #94A3B8' : 'none' }}>
				{status.state === 'done' && <CheckIcon size={24} />}
				{status.state === 'intervention' && <WrenchIcon size={24} color={look.color} />}
				{status.state === 'inventory' && <InventoryIcon size={24} color={look.color} />}
				{status.state === 'todo' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true"><circle cx="12" cy="12" r="7" /></svg>}
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3, textAlign: 'left' }}>
				<span style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{status.title}</span>
				{status.text && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{status.text}</span>}
			</span>
			{onOpen && <ChevronRight color={colors.primary} strokeWidth={2.8} />}
		</React.Fragment>
	)
	const style = { margin: '16px 20px 0', minHeight: 76, width: 'calc(100% - 40px)', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 18, border: '2px solid ' + (status.state === 'todo' ? colors.separator : look.background), backgroundImage: 'none', backgroundColor: BANNER_BACKGROUNDS[status.state], fontFamily: 'inherit' }
	if (!onOpen)
		return <div role="status" style={style}>{content}</div>
	return (
		<button type="button" className="field-row" aria-label={status.title + '. Voir le compte rendu'} onClick={onOpen} style={{ ...style, cursor: 'pointer' }}>
			{content}
		</button>
	)
}
const TEAM = {
	'Ménage': { background: '#1CB0C4', color: colors.text },
	'Technique': { background: '#8124FF', color: '#FFFFFF' }
}

const BroomIcon = ({ size = 22 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M14 4l-3 7" />
		<path d="M6 12h9l3 8H4z" />
		<path d="M9 16v4M13 16v4" />
	</svg>
)

const DownIcon = ({ up }) => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', transform: up ? 'rotate(180deg)' : 'none' }}>
		<path d="M5 9l7 7 7-7" />
	</svg>
)

function Section({ icon, color, iconColor, title, count, countStyle, children })
{
	return (
		<section style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
				<span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 999, background: color, color: iconColor || '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{icon}</span>
				<h2 style={{ flex: 1, fontSize: 20, fontWeight: 800, color: colors.text }}>{title}</h2>
				{count !== null && count !== undefined && <span style={{ minWidth: 36, height: 32, padding: '0 10px', borderRadius: 999, background: '#E8EBF5', color: colors.text, fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums', ...countStyle }}>{count}</span>}
			</div>
			{children}
		</section>
	)
}

function Empty({ children })
{
	return <p style={{ background: '#EEF0F5', borderRadius: 12, padding: '14px 16px', fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{children}</p>
}

function Group({ children })
{
	const items = React.Children.toArray(children).filter(Boolean)
	return (
		<div style={{ background: colors.surface, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
			{items.map((child, index) => <div key={child.key || index} style={{ borderTop: index > 0 ? '1px solid ' + colors.separator : 0 }}>{child}</div>)}
		</div>
	)
}

function Pill({ tone, icon, children })
{
	const look = tone === 'gap' ? { background: '#FEE2E2', color: '#7F1D1D' } : { background: '#DCFCE7', color: '#14532D' }
	return <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 8px', borderRadius: 999, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', ...look }}>{icon}{children}</span>
}

function authorOf(entry, user)
{
	const mine = !!user && entry.user === user._id
	return shortName(entry.user_name) || (mine ? 'Vous' : 'Un collègue')
}

function initials(entry)
{
	const name = (entry.user_name || '').trim()
	if (!name)
		return '?'
	return name.split(/\s+/).map(part => part.charAt(0)).slice(0, 2).join('').toUpperCase()
}

// onOpenIntervention(id) : ouvre l'intervention dans le module Interventions (/interventions/<id>)
export default function FieldTourReport({ pitch, entries, status, user, now, onOpenIntervention, onBack })
{
	const [history, setHistory] = useState(false)
	const [viewer, setViewer] = useState(null) // photo ouverte en plein écran : { url, alt }
	const closeViewer = useCallback(() => setViewer(null), [])

	useEffect(() => {
		const scroller = document.querySelector('.field-app')
		if (scroller)
			scroller.scrollTop = 0
	}, [])

	const byTime = (list) => list.slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
	const interventions = byTime(entries.filter(entry => entry.kind === 'intervention'))
	const photos = byTime(entries.filter(entry => entry.kind === 'photo'))
	const comments = byTime(entries.filter(entry => entry.kind === 'comment'))
	const inventory = byTime(entries.filter(entry => entry.kind === 'inventory' || !entry.kind)).pop() || null
	const inspection = byTime(entries.filter(entry => entry.kind === 'ok')).pop() || null
	const lines = reportLines(entries, user, now)
	const zone = zoneOf(pitch)
	const contextLine = [(pitch.location_category || '').trim(), zone ? zoneLabel(zone) : null].filter(Boolean).join(', ')

	return (
		<div className="field-screen" style={{ flex: 1, display: 'flex', flexDirection: 'column', marginTop: 'calc(-1 * max(12px, env(safe-area-inset-top)))' }}>
			<div style={{ position: 'sticky', top: 0, zIndex: 3, background: colors.background, paddingTop: 'max(12px, env(safe-area-inset-top))', borderBottom: '1px solid ' + colors.separator }}>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, minHeight: 56, padding: '0 20px 0 8px' }}>
					<button
						type="button"
						aria-label={'Retour à la fiche de ' + pitch.location}
						onClick={onBack}
						style={{ backgroundImage: 'none', backgroundColor: 'transparent', justifySelf: 'start', minHeight: 48, padding: '0 12px 0 4px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontFamily: 'inherit', cursor: 'pointer' }}
					>
						<ChevronLeft />
						{pitch.location}
					</button>
					<h1 style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, color: colors.text, textAlign: 'center' }}>Compte rendu</h1>
					<span />
				</div>
			</div>

			<div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
				<strong style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, color: colors.text }}>{pitch.location}</strong>
				{contextLine && <span style={{ fontSize: 17, fontWeight: 600, color: colors.text }}>{contextLine}</span>}
			</div>
			<StatusBanner status={{ ...status, text: latestLabel(entries, user, now) }} onOpen={null} />

			<div style={{ padding: '0 20px 32px' }}>
				<Section icon={<WrenchIcon size={20} />} color="#8124FF" title="Interventions" count={interventions.length || null}>
					{interventions.length === 0 && <Empty>Aucune intervention</Empty>}
					{interventions.length > 0 && (
						<Group>
							{interventions.map(entry => {
								const team = TEAM[entry.type] || TEAM['Technique']
								const open = (entry.intervention_id && onOpenIntervention) ? () => onOpenIntervention(entry.intervention_id) : null
								const Row = (open) ? 'button' : 'div'
								const rowProps = (open) ? { type: 'button', className: 'field-row', onClick: open } : {}
								return (
									<Row key={entry.id} {...rowProps} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', minHeight: 72, ...(open ? { width: '100%', border: 0, backgroundImage: 'none', backgroundColor: 'transparent', textAlign: 'left', fontFamily: 'inherit', color: colors.text, cursor: 'pointer' } : {}) }}>
										<span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: 999, background: team.background, color: team.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
											{entry.type === 'Ménage' ? <BroomIcon /> : <WrenchIcon size={20} color={team.color} />}
										</span>
										<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.25 }}>
											<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{'Équipe ' + entry.type}</span>
											<span style={{ fontSize: 19, fontWeight: 800, color: colors.text, overflowWrap: 'anywhere' }}>{entry.description || entry.label}</span>
											<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{'Créée par ' + authorOf(entry, user) + ', ' + whenLabel(entry.timestamp, now)}</span>
										</span>
										{open && <ChevronRight />}
									</Row>
								)
							})}
						</Group>
					)}
				</Section>

				<Section icon={<InventoryIcon size={20} />} color={colors.tour} iconColor={colors.primary} title="Inventaire" count={inventory && inventory.gaps ? plural(inventory.gaps, 'écart') : null} countStyle={{ background: '#FEE2E2', color: '#7F1D1D' }}>
					{!inventory && !inspection && <Empty>Pas d'inventaire dans ce tour</Empty>}
					{!inventory && inspection && <Empty>{'Inspection seule : rien à signaler. ' + authorOf(inspection, user) + ', ' + whenLabel(inspection.timestamp, now)}</Empty>}
					{inventory && (
						<Group>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 16px' }}>
								{inventory.gaps
									? <Pill tone="gap">{plural(inventory.gaps, 'écart')}</Pill>
									: <Pill icon={<CheckIcon size={16} />}>{inventory.same ? 'Identique au dernier inventaire' : 'Aucun écart'}</Pill>}
								<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{authorOf(inventory, user) + ', ' + whenLabel(inventory.timestamp, now)}</span>
							</div>
						</Group>
					)}
				</Section>

				<Section icon={<CameraIcon size={20} color="#FFFFFF" />} color="#0F766E" title="Photos" count={photos.length || null}>
					{photos.length === 0 && <Empty>Aucune photo</Empty>}
					{photos.length > 0 && (
						<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
							{photos.map((entry, index) => (
								<button
									key={entry.id}
									type="button"
									aria-label={'Afficher la photo ' + (index + 1) + ' en plein écran'}
									onClick={() => setViewer({ url: entry.photo.url || entry.photo.thumb, alt: entry.photo.description || 'Photo ' + (index + 1) })}
									style={{ backgroundImage: 'none', backgroundColor: '#DDE1EA', display: 'block', width: '100%', aspectRatio: '1 / 1', padding: 0, border: 0, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}
								>
									<img src={entry.photo.thumb || entry.photo.url} alt="" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
								</button>
							))}
						</div>
					)}
				</Section>

				<Section icon={<CommentIcon size={20} />} color="#B45309" title="Commentaires" count={comments.length || null}>
					{comments.length === 0 && <Empty>Aucun commentaire</Empty>}
					{comments.map(entry => (
						<div key={entry.id} style={{ background: colors.surface, borderRadius: 18, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 999, background: '#E8EBF5', color: colors.primary, fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{initials(entry)}</span>
								<span style={{ fontSize: 15, fontWeight: 800, color: colors.text }}>{authorOf(entry, user)}</span>
								<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{whenLabel(entry.comment.commentTime || entry.timestamp, now)}</span>
							</div>
							<p style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.45, color: colors.text, overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>{'« ' + entry.comment.comment.trim() + ' »'}</p>
						</div>
					))}
				</Section>

				<button
					type="button"
					aria-expanded={history}
					onClick={() => setHistory(!history)}
					style={{ backgroundImage: 'none', backgroundColor: colors.surface, marginTop: 32, width: '100%', minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 20px', borderRadius: 18, border: '2px solid ' + colors.border, color: colors.primary, fontSize: 17, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
				>
					{(history ? 'Masquer l\'historique' : 'Voir l\'historique') + ' (' + lines.length + ')'}
					<DownIcon up={history} />
				</button>
				{/* Fil d'actualité (demande d'Alexandra du 24/10/2026) : le plus récent en haut, un point par
				    action relié par un trait ; le point du haut est plein */}
				{history && (
					<ol style={{ listStyle: 'none', margin: '12px 0 0', padding: '16px 16px 4px', background: colors.surface, borderRadius: 18, display: 'flex', flexDirection: 'column' }}>
						{lines.slice().reverse().map((line, index, all) => (
							<li key={line.id} style={{ display: 'flex', gap: 14 }}>
								<span aria-hidden="true" style={{ position: 'relative', width: 16, flex: 'none', display: 'flex', justifyContent: 'center' }}>
									{index < all.length - 1 && <span style={{ position: 'absolute', top: 10, bottom: -10, width: 2, background: colors.border }} />}
									<span style={{ position: 'relative', marginTop: 5, width: 14, height: 14, borderRadius: 999, boxSizing: 'border-box', background: index === 0 ? colors.primary : colors.surface, border: '3px solid ' + colors.primary }} />
								</span>
								<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3, paddingBottom: 16 }}>
									<span style={{ fontSize: 17, fontWeight: 700, color: colors.text, overflowWrap: 'break-word' }}>{line.label}</span>
									<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{line.meta}</span>
								</span>
							</li>
						))}
					</ol>
				)}
			</div>

			<div style={{ position: 'sticky', bottom: 0, zIndex: 2, marginTop: 'auto', display: 'flex', padding: '12px 20px', paddingBottom: 'max(20px, env(safe-area-inset-bottom))', background: colors.background, borderTop: '1px solid ' + colors.separator }}>
				<SecondaryButton onClick={onBack}>{'Retour à ' + pitch.location}</SecondaryButton>
			</div>
			{viewer && <PhotoViewer url={viewer.url} alt={viewer.alt} onClose={closeViewer} />}
		</div>
	)
}
